import React, { useState, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl, apiFetch } from '../../config/api';
import './SupervisorCostTracker.css';
import { formatAppDate } from '../../utils/dateUtils';

const SupervisorCostTracker = React.forwardRef((props, ref) => {
  const { t } = useTranslation('supervisor');
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);

  // Data states
  const [costOverview, setCostOverview] = useState(null);
  const [orderCosts, setOrderCosts] = useState([]);
  const [categoryCosts, setCategoryCosts] = useState([]);
  const [wasteCosts, setWasteCosts] = useState(null);
  const [costTrends, setCostTrends] = useState(null);
  const [topDrivers, setTopDrivers] = useState([]);

  // Production order details state
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, [period]);

  // Expose refresh method to parent
  useImperativeHandle(ref, () => ({
    refresh: fetchAllData
  }));

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchCostOverview(),
        fetchOrderCosts(),
        fetchCategoryCosts(),
        fetchWasteCosts(),
        fetchCostTrends(),
        fetchTopDrivers()
      ]);
    } catch (error) {
      console.error('Error fetching cost data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCostOverview = async () => {
    const response = await apiFetch(getApiUrl(`/api/production-costs/overview?period=${period}`));
    if (response.ok) {
      const data = await response.json();
      setCostOverview(data);
    }
  };

  const fetchOrderCosts = async () => {
    const response = await apiFetch(getApiUrl(`/api/production-costs/by-order?period=${period}`));
    if (response.ok) {
      const data = await response.json();
      setOrderCosts(data);
    }
  };

  const fetchCategoryCosts = async () => {
    const response = await apiFetch(getApiUrl(`/api/production-costs/by-category?period=${period}`));
    if (response.ok) {
      const data = await response.json();
      setCategoryCosts(data);
    }
  };

  const fetchWasteCosts = async () => {
    const response = await apiFetch(getApiUrl(`/api/production-costs/waste?period=${period}`));
    if (response.ok) {
      const data = await response.json();
      setWasteCosts(data);
    }
  };

  const fetchCostTrends = async () => {
    const response = await apiFetch(getApiUrl(`/api/production-costs/trends?period=${period}&period_type=daily`));
    if (response.ok) {
      const data = await response.json();
      setCostTrends(data);
    }
  };

  const fetchTopDrivers = async () => {
    const response = await apiFetch(getApiUrl(`/api/production-costs/top-drivers?period=${period}&limit=10`));
    if (response.ok) {
      const data = await response.json();
      setTopDrivers(data);
    }
  };

  const fetchOrderDetails = async (productionOrderId) => {
    setLoadingDetails(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/production-costs/order/${productionOrderId}/details`));
      if (response.ok) {
        const data = await response.json();
        setOrderDetails(data);
        setSelectedOrderId(productionOrderId);
        setActiveTab('order-details');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleOrderClick = (productionOrderId) => {
    fetchOrderDetails(productionOrderId);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  if (loading && !costOverview) {
    return <div className="cost-tracker-loading">{t('costTracker.loading')}</div>;
  }

  return (
    <div className="cost-tracker">
      {/* Header */}
      <div className="cost-tracker-header">
        <h2>💰 {t('costTracker.title')}</h2>
        <div className="header-actions">
          <select
            className="period-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <option value="today">{t('costTracker.periods.today')}</option>
            <option value="week">{t('costTracker.periods.thisWeek')}</option>
            <option value="month">{t('costTracker.periods.thisMonth')}</option>
            <option value="quarter">{t('costTracker.periods.thisQuarter')}</option>
            <option value="year">{t('costTracker.periods.thisYear')}</option>
            <option value="last_month">{t('costTracker.periods.lastMonth')}</option>
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      {costOverview && (
        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-label">{t('costTracker.kpis.totalMaterialCosts')}</div>
            <div className="kpi-value">{formatCurrency(costOverview.total_material_cost)}</div>
            <div className="kpi-sublabel">
              {period === 'month' ? t('costTracker.kpis.monthToDate') : period.charAt(0).toUpperCase() + period.slice(1)}
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-label">{t('costTracker.kpis.wasteCosts')}</div>
            <div className="kpi-value">{formatCurrency(costOverview.total_waste_cost)}</div>
            <div className="kpi-sublabel">
              {formatNumber(costOverview.waste_percentage)}% {t('costTracker.kpis.ofMaterial')}
            </div>
          </div>

          <div className="kpi-card">
            {/* One cost per unit over several products means nothing (#372): it is per order, in its tab */}
            <div className="kpi-label">{t('costTracker.kpis.unitsProducedLabel')}</div>
            <div className="kpi-value">{formatNumber(costOverview.total_units_produced)}</div>
            <div className="kpi-sublabel">{t('costTracker.kpis.unitCostByOrder')}</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-label">{t('costTracker.kpis.materialCategories')}</div>
            <div className="kpi-value">{Object.keys(costOverview.material_cost_by_category).length}</div>
            <div className="kpi-sublabel">
              {t('costTracker.kpis.activeCategories')}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="cost-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          {t('costTracker.tabs.overview')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          {t('costTracker.tabs.byProductionOrder')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          {t('costTracker.tabs.byCategory')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'waste' ? 'active' : ''}`}
          onClick={() => setActiveTab('waste')}
        >
          {t('costTracker.tabs.wasteAnalysis')}
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            {/* Category Breakdown */}
            <div className="cost-card">
              <h3>{t('costTracker.overview.costByCategory')}</h3>
              <table className="cost-table">
                <thead>
                  <tr>
                    <th>{t('costTracker.headers.category')}</th>
                    <th>{t('costTracker.headers.totalCost')}</th>
                    <th>{t('costTracker.headers.percentOfTotal')}</th>
                    <th>{t('costTracker.headers.transactions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryCosts.map((cat) => (
                    <tr key={cat.category}>
                      <td>
                        <span className={`category-badge category-${cat.category.toLowerCase()}`}>
                          {cat.category}
                        </span>
                      </td>
                      <td>{formatCurrency(cat.total_cost)}</td>
                      <td>{formatNumber(cat.percentage_of_total)}%</td>
                      <td>{cat.transaction_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Top Cost Drivers */}
            <div className="cost-card">
              <h3>{t('costTracker.overview.topCostDrivers')}</h3>
              <table className="cost-table">
                <thead>
                  <tr>
                    <th>{t('costTracker.headers.material')}</th>
                    <th>{t('costTracker.headers.code')}</th>
                    <th>{t('costTracker.headers.type')}</th>
                    <th>{t('costTracker.headers.totalCost')}</th>
                    <th>{t('costTracker.headers.quantity')}</th>
                    <th>{t('costTracker.headers.avgUnitCost')}</th>
                    <th>{t('costTracker.headers.percentOfTotal')}</th>
                  </tr>
                </thead>
                <tbody>
                  {topDrivers.map((driver) => (
                    <tr key={driver.item_id}>
                      <td>{driver.item_name}</td>
                      <td className="code-cell">{driver.item_code}</td>
                      <td>
                        <span className={`category-badge category-${driver.item_type.toLowerCase()}`}>
                          {driver.item_type}
                        </span>
                      </td>
                      <td className="cost-cell">{formatCurrency(driver.total_cost)}</td>
                      <td>{formatNumber(driver.total_quantity)} {driver.unit_of_measure}</td>
                      <td>{formatCurrency(driver.average_unit_cost)}</td>
                      <td>{formatNumber(driver.percentage_of_total)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="orders-tab">
            <div className="cost-card">
              <h3>{t('costTracker.orders.costByProductionOrder')}</h3>
              <table className="cost-table">
                <thead>
                  <tr>
                    <th>{t('costTracker.headers.poNumber')}</th>
                    <th>{t('costTracker.headers.product')}</th>
                    <th>{t('costTracker.headers.ordered')}</th>
                    <th>{t('costTracker.headers.produced')}</th>
                    <th>{t('costTracker.headers.materialCost')}</th>
                    <th>{t('costTracker.headers.wasteCost')}</th>
                    <th>{t('costTracker.headers.costPerUnit')}</th>
                    <th>{t('costTracker.headers.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {orderCosts.map((order) => (
                    <tr
                      key={order.production_order_line_id || `po-${order.production_order_id}`}
                      onClick={() => handleOrderClick(order.production_order_id)}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#0f172a'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <td className="code-cell">{order.po_number}</td>
                      <td>{order.product_name || 'N/A'}</td>
                      <td>{formatNumber(order.order_quantity)}</td>
                      <td>{formatNumber(order.produced_quantity)}</td>
                      <td className="cost-cell">{formatCurrency(order.total_material_cost)}</td>
                      <td className="cost-cell">{formatCurrency(order.waste_cost)}</td>
                      <td className="cost-cell">{formatCurrency(order.material_cost_per_unit)}</td>
                      <td>
                        <span className={`status-badge status-${order.status.toLowerCase()}`}>
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'categories' && (
          <div className="categories-tab">
            <div className="cost-card">
              <h3>{t('costTracker.categories.materialCostAnalysis')}</h3>
              <div className="category-grid">
                {categoryCosts.map((cat) => (
                  <div key={cat.category} className="category-item">
                    <div className={`category-icon category-${cat.category.toLowerCase()}`}>
                      {cat.category.charAt(0)}
                    </div>
                    <div className="category-details">
                      <div className="category-name">{cat.category}</div>
                      <div className="category-cost">{formatCurrency(cat.total_cost)}</div>
                      <div className="category-percent">{formatNumber(cat.percentage_of_total)}% {t('costTracker.kpis.ofTotal')}</div>
                      <div className="category-count">{cat.transaction_count} {t('costTracker.headers.transactions')}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'waste' && wasteCosts && (
          <div className="waste-tab">
            <div className="cost-card">
              <h3>{t('costTracker.waste.wasteAnalysis')}</h3>
              <div className="waste-summary">
                <div className="waste-kpi">
                  <div className="waste-label">{t('costTracker.waste.totalWasteCost')}</div>
                  <div className="waste-value">{formatCurrency(wasteCosts.total_waste_cost)}</div>
                </div>
                <div className="waste-kpi">
                  <div className="waste-label">{t('costTracker.waste.wastePercentage')}</div>
                  <div className="waste-value">{formatNumber(wasteCosts.waste_percentage)}%</div>
                </div>
              </div>

              <table className="cost-table">
                <thead>
                  <tr>
                    <th>{t('costTracker.headers.material')}</th>
                    <th>{t('costTracker.headers.code')}</th>
                    <th>{t('costTracker.waste.quantityWasted')}</th>
                    <th>{t('costTracker.headers.wasteCost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {wasteCosts.waste_by_material.map((waste, idx) => (
                    <tr key={idx}>
                      <td>{waste.material_name}</td>
                      <td className="code-cell">{waste.material_code}</td>
                      <td>{formatNumber(waste.quantity)} {waste.unit_of_measure}</td>
                      <td className="cost-cell">{formatCurrency(waste.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'order-details' && orderDetails && (
          <div className="order-details-tab">
            <div className="cost-card" style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3>{t('costTracker.orderDetails.title')}</h3>
                <button
                  onClick={() => setActiveTab('orders')}
                  style={{
                    background: '#64748b',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  ← {t('costTracker.orderDetails.backToOrders')}
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div>
                  <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '0.25rem' }}>{t('costTracker.headers.poNumber')}</p>
                  <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#60a5fa' }}>{orderDetails.po_number}</p>
                </div>
                <div>
                  <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '0.25rem' }}>{t('costTracker.orderDetails.customer')}</p>
                  <p style={{ fontSize: '1.1rem', fontWeight: '500' }}>{orderDetails.customer_name || 'N/A'}</p>
                </div>
                <div>
                  <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '0.25rem' }}>{t('costTracker.headers.product')}</p>
                  <p style={{ fontSize: '1.1rem', fontWeight: '500' }}>{orderDetails.product_name || 'N/A'}</p>
                </div>
                <div>
                  <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '0.25rem' }}>{t('costTracker.orderDetails.orderedQuantity')}</p>
                  <p style={{ fontSize: '1.2rem', fontWeight: '500' }}>{formatNumber(orderDetails.order_quantity)}</p>
                </div>
                <div>
                  <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '0.25rem' }}>{t('costTracker.orderDetails.producedQuantity')}</p>
                  <p style={{ fontSize: '1.2rem', fontWeight: '500' }}>{formatNumber(orderDetails.produced_quantity)}</p>
                </div>
                <div>
                  <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '0.25rem' }}>{t('costTracker.headers.status')}</p>
                  <span className={`status-badge status-${orderDetails.status.toLowerCase()}`}>
                    {orderDetails.status}
                  </span>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', padding: '1rem', background: '#0f172a', borderRadius: '8px' }}>
                <div>
                  <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '0.25rem' }}>{t('costTracker.headers.materialCost')}</p>
                  <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>{formatCurrency(orderDetails.total_material_cost)}</p>
                </div>
                <div>
                  <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '0.25rem' }}>{t('costTracker.headers.wasteCost')}</p>
                  <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>{formatCurrency(orderDetails.waste_cost)}</p>
                </div>
                <div>
                  <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '0.25rem' }}>{t('costTracker.headers.costPerUnit')}</p>
                  <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#60a5fa' }}>{formatCurrency(orderDetails.cost_per_unit)}</p>
                </div>
              </div>
            </div>

            <div className="cost-card" style={{ marginBottom: '1rem' }}>
              <h3>{t('costTracker.orderDetails.materialBreakdown')}</h3>
              {orderDetails.material_breakdown.map((category, idx) => (
                <div key={idx} style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span className={`category-badge category-${category.category.toLowerCase()}`}>
                      {category.category}
                    </span>
                    <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#10b981' }}>
                      {formatCurrency(category.total_cost)}
                    </span>
                  </div>
                  <table className="cost-table">
                    <thead>
                      <tr>
                        <th>{t('costTracker.headers.material')}</th>
                        <th>{t('costTracker.headers.code')}</th>
                        <th>{t('costTracker.headers.quantity')}</th>
                        <th>{t('costTracker.headers.unitCost')}</th>
                        <th>{t('costTracker.headers.totalCost')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {category.items.map((item, itemIdx) => (
                        <tr key={itemIdx}>
                          <td>{item.item_name}</td>
                          <td className="code-cell">{item.item_code}</td>
                          <td>{formatNumber(item.quantity)} {item.unit_of_measure}</td>
                          <td className="cost-cell">{formatCurrency(item.unit_cost)}</td>
                          <td className="cost-cell">{formatCurrency(item.total_cost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>

            <div className="cost-card">
              <h3>{t('costTracker.orderDetails.transactionHistory')}</h3>
              <table className="cost-table">
                <thead>
                  <tr>
                    <th>{t('costTracker.headers.date')}</th>
                    <th>{t('costTracker.headers.type')}</th>
                    <th>{t('costTracker.headers.material')}</th>
                    <th>{t('costTracker.headers.quantity')}</th>
                    <th>{t('costTracker.headers.unitCost')}</th>
                    <th>{t('costTracker.headers.totalValue')}</th>
                  </tr>
                </thead>
                <tbody>
                  {orderDetails.material_transactions.map((trans) => (
                    <tr key={trans.transaction_id}>
                      <td>{formatAppDate(trans.transaction_date, 'datetime')}</td>
                      <td>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          background: trans.transaction_type === 'OUT' ? '#10b98120' : '#ef444420',
                          color: trans.transaction_type === 'OUT' ? '#10b981' : '#ef4444'
                        }}>
                          {trans.transaction_type}
                        </span>
                      </td>
                      <td>{trans.item_code} - {trans.item_name}</td>
                      <td>{formatNumber(trans.quantity)}</td>
                      <td className="cost-cell">{formatCurrency(trans.unit_cost)}</td>
                      <td className="cost-cell">{formatCurrency(trans.total_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

SupervisorCostTracker.displayName = 'SupervisorCostTracker';

export default SupervisorCostTracker;
